import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit, Trash2, Eye, EyeOff, MapPin, CalendarDays } from 'lucide-react'
import { useEvents, useDeleteEvent, useUpdateEvent } from '@/hooks/use-events'
import { usePlanLimit } from '@/hooks/use-plan-limit'
import { formatDate } from '@/lib/utils'
import type { Tables } from '@/types/supabase'
import { FeatureGate } from '@/components/feature-gate'

type Event = Tables<'events'>

function formatMonthYear(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('id-ID', {
    month: 'long',
    year: 'numeric',
  })
}

function formatEventDate(event: Event): string {
  const start = formatDate(event.starts_at, { dateStyle: 'medium' })
  if (!event.ends_at) return start
  const startDate = new Date(event.starts_at).toDateString()
  const endDate = new Date(event.ends_at).toDateString()
  if (startDate === endDate) return start
  return `${start} – ${formatDate(event.ends_at, { dateStyle: 'medium' })}`
}

function getMonthKey(dateStr: string): string {
  const d = new Date(dateStr)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export default function EventsPage() {
  const navigate = useNavigate()
  const { data: events = [], isLoading } = useEvents()
  const deleteEvent = useDeleteEvent()
  const updateEvent = useUpdateEvent()
  const { limit: eventLimit, isUnlimited } = usePlanLimit('max_events')

  const [deleteTarget, setDeleteTarget] = useState<Event | null>(null)

  const atLimit = !isUnlimited && eventLimit !== null && events.length >= eventLimit

  // Group events by month, preserving sort order from server (starts_at ASC/DESC)
  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; events: Event[] }>()
    for (const ev of events) {
      const key = getMonthKey(ev.starts_at)
      if (!map.has(key)) {
        map.set(key, { label: formatMonthYear(ev.starts_at), events: [] })
      }
      map.get(key)!.events.push(ev)
    }
    return Array.from(map.entries())
  }, [events])

  if (isLoading) return <p className="text-muted-foreground">Memuat event...</p>

  return (
    <FeatureGate feature="events">
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Event</h1>
          <p className="text-muted-foreground">Kelola jadwal event idol group Anda</p>
          {!isUnlimited && eventLimit !== null && (
            <p className="mt-1 text-xs text-muted-foreground">
              {events.length}/{eventLimit} event
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => !atLimit && navigate('/events/new')}
            disabled={atLimit}
            title={atLimit ? `Batas ${eventLimit} event tercapai. Upgrade plan untuk menambah lebih.` : undefined}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Tambah Event
          </button>
          {atLimit && (
            <p className="text-xs text-destructive">
              Batas plan tercapai.{' '}
              <a href="/settings/subscription" className="underline hover:text-destructive/80">
                Upgrade plan
              </a>
            </p>
          )}
        </div>
      </div>

      {/* Empty state */}
      {events.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          Belum ada event. Tambahkan event pertama Anda.
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([key, { label, events: monthEvents }]) => (
            <section key={key}>
              {/* Month separator */}
              <div className="mb-4 flex items-center gap-3">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  {label}
                </h2>
                <div className="h-px flex-1 bg-border" />
              </div>

              {/* Cards grid */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {monthEvents.map((event) => (
                  <div
                    key={event.id}
                    className="group relative flex flex-col rounded-2xl border border-border bg-card overflow-hidden shadow-sm transition-shadow hover:shadow-md"
                  >
                    {/* Clickable area → detail page */}
                    <div
                      className="flex flex-col cursor-pointer"
                      onClick={() => navigate(`/events/${event.id}`)}
                    >
                      {/* Thumbnail */}
                      <div className="relative aspect-video w-full overflow-hidden bg-muted">
                        {event.cover_url ? (
                          <img
                            src={event.cover_url}
                            alt={event.title}
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <CalendarDays className="h-10 w-10 text-muted-foreground/30" />
                          </div>
                        )}

                        {/* Published badge */}
                        <div className="absolute top-2 right-2">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold shadow ${
                              event.is_published
                                ? 'bg-green-500 text-white'
                                : 'bg-yellow-400 text-yellow-900'
                            }`}
                          >
                            {event.is_published ? 'Publik' : 'Draft'}
                          </span>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="flex flex-col gap-1 px-4 pt-3 pb-2">
                        <p className="line-clamp-2 text-sm font-semibold leading-snug">
                          {event.title}
                        </p>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <CalendarDays className="h-3 w-3 shrink-0" />
                          <span>{formatEventDate(event)}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions — stopPropagation so card click doesn't fire */}
                    <div
                      className="flex items-center justify-end gap-0.5 px-3 pb-2 pt-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() =>
                          updateEvent.mutate({
                            id: event.id,
                            is_published: !event.is_published,
                          })
                        }
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                        title={event.is_published ? 'Sembunyikan' : 'Publikasikan'}
                      >
                        {event.is_published ? (
                          <EyeOff className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </button>
                      <button
                        onClick={() => navigate(`/events/${event.id}/edit`)}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                        title="Edit event"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(event)}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                        title="Hapus event"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null) }}
        >
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl space-y-4">
            <h2 className="font-semibold">Hapus Event?</h2>
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">{deleteTarget.title}</strong> akan dihapus
              secara permanen beserta lineup dan setlist-nya.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
              >
                Batal
              </button>
              <button
                onClick={() =>
                  deleteEvent.mutate(deleteTarget.id, {
                    onSuccess: () => setDeleteTarget(null),
                  })
                }
                disabled={deleteEvent.isPending}
                className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-white hover:bg-destructive/90 disabled:opacity-50"
              >
                {deleteEvent.isPending ? 'Menghapus...' : 'Hapus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </FeatureGate>
  )
}
